import { FormEvent, useEffect, useState } from "react";
import { Copy, Download, Edit, Eye, FileSpreadsheet, Layers, Percent, Plus, RefreshCw, Save, Search, Ticket, Truck, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminShippingCoupon,
  AdminShippingCouponBatchPayload,
  AdminShippingCouponBatchSummary,
  AdminShippingCouponPayload,
  AdminUser,
  COUPON_DISCOUNT_TYPES,
  CouponDiscountType,
  PaginationMeta,
  deleteAdminShippingCoupon,
  deleteAdminShippingCouponBatch,
  exportAdminShippingCouponBatch,
  generateAdminShippingCouponBatch,
  getAdminShippingCouponBatch,
  getCurrentAdmin,
  listAdminShippingCouponBatches,
  listAdminShippingCoupons,
  logout,
  saveAdminShippingCoupon,
} from "./api";
import { useConfirmDialog } from "./components/ConfirmDialog";
import { AdminRadioCardGroup, AdminSwitchCard } from "./components/AdminChoiceControl";
import Pagination from "./components/Pagination";

const defaultPagination: PaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

type CreateMode = "manual" | "batch";

const discountTypeLabels: Record<CouponDiscountType, string> = { FreeShipping: "免運", FixedAmount: "固定金額折抵", Percentage: "依比例折扣" };

/** 折扣摘要文字，用於列表與批次結果表格顯示（Percentage 的 DiscountAmount 是萬分比，顯示時要換算回百分比）。 */
function describeDiscount(item: Pick<AdminShippingCoupon, "DiscountType" | "DiscountAmount" | "MaxDiscountAmount">): string {
  if (item.DiscountType === "FixedAmount") return `折抵 NT$ ${item.DiscountAmount.toLocaleString()}`;
  if (item.DiscountType === "Percentage") {
    const percent = (item.DiscountAmount / 100).toLocaleString();
    return item.MaxDiscountAmount !== null ? `${percent}%（上限 NT$ ${item.MaxDiscountAmount.toLocaleString()}）` : `${percent}%`;
  }
  return "免運費";
}

type FormState = {
  Code: string;
  Description: string;
  DiscountType: CouponDiscountType;
  DiscountAmount: string;
  MaxDiscountAmount: string;
  MinOrderAmount: string;
  MaxRedemptions: string;
  StartAt: string;
  EndAt: string;
  IsActive: boolean;
};

const emptyForm: FormState = {
  Code: "",
  Description: "",
  DiscountType: "FreeShipping",
  DiscountAmount: "",
  MaxDiscountAmount: "",
  MinOrderAmount: "",
  MaxRedemptions: "",
  StartAt: "",
  EndAt: "",
  IsActive: true,
};

type BatchFormState = {
  BatchLabel: string;
  Quantity: string;
  CodePrefix: string;
  CodeLength: string;
  DiscountType: CouponDiscountType;
  DiscountAmount: string;
  MaxDiscountAmount: string;
  MaxRedemptionsPerCode: string;
  MinOrderAmount: string;
  StartAt: string;
  EndAt: string;
  IsActive: boolean;
};

const emptyBatchForm: BatchFormState = {
  BatchLabel: "",
  Quantity: "50",
  CodePrefix: "",
  CodeLength: "8",
  DiscountType: "FreeShipping",
  DiscountAmount: "",
  MaxDiscountAmount: "",
  MaxRedemptionsPerCode: "1",
  MinOrderAmount: "",
  StartAt: "",
  EndAt: "",
  IsActive: true,
};

function toDatetimeLocal(value: string | null): string {
  if (!value) return "";
  return value.slice(0, 16);
}

function itemToForm(item: AdminShippingCoupon): FormState {
  return {
    Code: item.Code,
    Description: item.Description || "",
    DiscountType: item.DiscountType,
    // Percentage 儲存為萬分比（例如 1000 代表 10%），編輯表單顯示時換算回一般人習慣輸入的百分比數字。
    DiscountAmount: item.DiscountType === "Percentage" ? String(item.DiscountAmount / 100) : item.DiscountType === "FixedAmount" ? String(item.DiscountAmount) : "",
    MaxDiscountAmount: item.MaxDiscountAmount !== null ? String(item.MaxDiscountAmount) : "",
    MinOrderAmount: item.MinOrderAmount !== null ? String(item.MinOrderAmount) : "",
    MaxRedemptions: item.MaxRedemptions !== null ? String(item.MaxRedemptions) : "",
    StartAt: toDatetimeLocal(item.StartAt),
    EndAt: toDatetimeLocal(item.EndAt),
    IsActive: item.IsActive,
  };
}

function formatDateTime(value: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("zh-TW", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** 把代碼清單組成一份 CSV 文字並觸發瀏覽器下載，供批次產生後或事後檢視時匯出給行銷／客服人員使用。 */
function downloadCouponsCsv(fileName: string, items: AdminShippingCoupon[]) {
  const header = "代碼,折扣內容,最低訂單金額,可用次數上限,已使用次數,狀態";
  const rows = items.map((item) => {
    const minOrderAmount = item.MinOrderAmount !== null ? String(item.MinOrderAmount) : "不限";
    const maxRedemptions = item.MaxRedemptions !== null ? String(item.MaxRedemptions) : "不限";
    const status = item.IsActive ? "啟用" : "停用";
    return [item.Code, describeDiscount(item), minOrderAmount, maxRedemptions, String(item.RedemptionCount), status].join(",");
  });
  const csvContent = "﻿" + [header, ...rows].join("\r\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const inputClass = "h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";

export default function AdminShippingCoupons() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<AdminShippingCoupon[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [editingItem, setEditingItem] = useState<AdminShippingCoupon | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  // 「新增」視窗裡的兩種模式：手動輸入單一代碼（沿用原本流程），或系統自動配置一整批不重複代碼。
  const [createMode, setCreateMode] = useState<CreateMode>("manual");
  const [batchForm, setBatchForm] = useState<BatchFormState>(emptyBatchForm);
  const [generatingBatch, setGeneratingBatch] = useState(false);
  const [batchResult, setBatchResult] = useState<{ BatchId: string; BatchLabel: string; Items: AdminShippingCoupon[] } | null>(null);
  const [copyFeedback, setCopyFeedback] = useState("");
  const [exportingBatchId, setExportingBatchId] = useState<string | null>(null);

  // 批次總覽：獨立於主列表之外，方便管理者一次看到「哪些活動批次發了多少組、用掉多少組」。
  const [batches, setBatches] = useState<AdminShippingCouponBatchSummary[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(true);
  const [viewingBatch, setViewingBatch] = useState<{ BatchId: string; BatchLabel: string | null; Items: AdminShippingCoupon[] } | null>(null);
  const [viewingBatchLoading, setViewingBatchLoading] = useState(false);

  async function load(filters = { Status: status, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminShippingCoupons(filters);
      setItems(data.Items);
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "優惠碼資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  async function loadBatches() {
    setBatchesLoading(true);
    try {
      const data = await listAdminShippingCouponBatches();
      setBatches(data.Batches);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "批次總覽載入失敗。");
    } finally {
      setBatchesLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", Keyword: "", Page: 1, PerPage: pagination.PerPage });
    loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  function handlePageChange(page: number) {
    load({ Status: status, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage });
  }

  function openCreateForm() {
    setEditingItem(null);
    setForm(emptyForm);
    setBatchForm(emptyBatchForm);
    setCreateMode("manual");
    setBatchResult(null);
    setFormMessage("");
    setFormOpen(true);
  }

  function openEditForm(item: AdminShippingCoupon) {
    setEditingItem(item);
    setForm(itemToForm(item));
    setCreateMode("manual");
    setBatchResult(null);
    setFormMessage("");
    setFormOpen(true);
  }

  function closeForm() {
    if (saving || generatingBatch) return;
    setFormOpen(false);
    setEditingItem(null);
    setForm(emptyForm);
    setBatchForm(emptyBatchForm);
    setBatchResult(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.Code.trim()) { setFormMessage("請輸入優惠碼代碼。"); return; }
    if (form.StartAt && form.EndAt && form.StartAt > form.EndAt) { setFormMessage("開始時間不可晚於結束時間。"); return; }
    if (form.DiscountType !== "FreeShipping" && (!form.DiscountAmount.trim() || Number(form.DiscountAmount) <= 0)) {
      setFormMessage(form.DiscountType === "Percentage" ? "請輸入折扣比例。" : "請輸入折抵金額。");
      return;
    }

    const payload: AdminShippingCouponPayload = {
      Code: form.Code.trim().toUpperCase(),
      Description: form.Description.trim() || undefined,
      DiscountType: form.DiscountType,
      // Percentage 在畫面上輸入的是一般人習慣的百分比數字（例如 10 代表 10%），送給後端前換算成萬分比儲存格式（* 100）。
      DiscountAmount:
        form.DiscountType === "Percentage" ? Math.round(Number(form.DiscountAmount || 0) * 100)
          : form.DiscountType === "FixedAmount" ? Math.round(Number(form.DiscountAmount || 0))
          : 0,
      MaxDiscountAmount: form.DiscountType === "Percentage" && form.MaxDiscountAmount.trim() !== "" ? Number(form.MaxDiscountAmount) : null,
      MinOrderAmount: form.MinOrderAmount.trim() !== "" ? Number(form.MinOrderAmount) : null,
      MaxRedemptions: form.MaxRedemptions.trim() !== "" ? Number(form.MaxRedemptions) : null,
      StartAt: form.StartAt || null,
      EndAt: form.EndAt || null,
      IsActive: form.IsActive,
    };

    setSaving(true);
    setFormMessage("");
    try {
      await saveAdminShippingCoupon(editingItem?.ShippingCouponId || null, payload);
      closeForm();
      await load();
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "優惠碼儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: AdminShippingCoupon) {
    const confirmed = await confirm({
      title: "刪除這組優惠碼？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{item.Code}</strong> 嗎？刪除後客人將無法再使用此優惠碼，已使用過的訂單不受影響。
        </>
      ),
      confirmText: "刪除優惠碼",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminShippingCoupon(item.ShippingCouponId);
      await load();
      await loadBatches();
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "優惠碼刪除失敗。");
    }
  }

  async function handleGenerateBatch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!batchForm.BatchLabel.trim()) { setFormMessage("請輸入批次（活動）名稱。"); return; }
    const quantity = Number(batchForm.Quantity);
    if (!Number.isFinite(quantity) || quantity < 1 || quantity > 500) { setFormMessage("產生數量請介於 1～500 組之間。"); return; }
    if (batchForm.StartAt && batchForm.EndAt && batchForm.StartAt > batchForm.EndAt) { setFormMessage("開始時間不可晚於結束時間。"); return; }
    if (batchForm.DiscountType !== "FreeShipping" && (!batchForm.DiscountAmount.trim() || Number(batchForm.DiscountAmount) <= 0)) {
      setFormMessage(batchForm.DiscountType === "Percentage" ? "請輸入折扣比例。" : "請輸入折抵金額。");
      return;
    }

    const payload: AdminShippingCouponBatchPayload = {
      BatchLabel: batchForm.BatchLabel.trim(),
      Quantity: quantity,
      CodeLength: batchForm.CodeLength.trim() !== "" ? Number(batchForm.CodeLength) : undefined,
      CodePrefix: batchForm.CodePrefix.trim() || undefined,
      DiscountType: batchForm.DiscountType,
      DiscountAmount:
        batchForm.DiscountType === "Percentage" ? Math.round(Number(batchForm.DiscountAmount || 0) * 100)
          : batchForm.DiscountType === "FixedAmount" ? Math.round(Number(batchForm.DiscountAmount || 0))
          : 0,
      MaxDiscountAmount: batchForm.DiscountType === "Percentage" && batchForm.MaxDiscountAmount.trim() !== "" ? Number(batchForm.MaxDiscountAmount) : null,
      MaxRedemptionsPerCode: batchForm.MaxRedemptionsPerCode.trim() !== "" ? Number(batchForm.MaxRedemptionsPerCode) : null,
      MinOrderAmount: batchForm.MinOrderAmount.trim() !== "" ? Number(batchForm.MinOrderAmount) : null,
      StartAt: batchForm.StartAt || null,
      EndAt: batchForm.EndAt || null,
      IsActive: batchForm.IsActive,
    };

    setGeneratingBatch(true);
    setFormMessage("");
    try {
      const result = await generateAdminShippingCouponBatch(payload);
      setBatchResult(result);
      await load();
      await loadBatches();
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "批次產生失敗，請稍後再試。");
    } finally {
      setGeneratingBatch(false);
    }
  }

  async function handleCopyCodes(codes: string[]) {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopyFeedback("已複製到剪貼簿！");
      setTimeout(() => setCopyFeedback(""), 2500);
    } catch {
      setCopyFeedback("複製失敗，請手動選取後複製。");
    }
  }

  async function handleExportBatchExcel(batchId: string) {
    setExportingBatchId(batchId);
    try {
      await exportAdminShippingCouponBatch(batchId);
    } catch (error) {
      setCopyFeedback(error instanceof Error ? error.message : "匯出失敗，請稍後再試。");
    } finally {
      setExportingBatchId(null);
    }
  }

  async function handleViewBatch(batch: AdminShippingCouponBatchSummary) {
    setViewingBatchLoading(true);
    setViewingBatch({ BatchId: batch.BatchId, BatchLabel: batch.BatchLabel, Items: [] });
    try {
      const data = await getAdminShippingCouponBatch(batch.BatchId);
      setViewingBatch(data);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "批次代碼載入失敗。");
      setViewingBatch(null);
    } finally {
      setViewingBatchLoading(false);
    }
  }

  async function handleDeleteBatch(batch: AdminShippingCouponBatchSummary) {
    const confirmed = await confirm({
      title: "刪除整個批次？",
      description: (
        <>
          確定要刪除批次「<strong className="font-bold text-[#203d32]">{batch.BatchLabel || batch.BatchId}</strong>」底下全部 {batch.TotalCodes} 組代碼嗎？
          刪除後所有尚未使用的代碼會立即失效，已使用過的訂單不受影響，此動作無法復原。
        </>
      ),
      confirmText: "刪除整批",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminShippingCouponBatch(batch.BatchId);
      await load();
      await loadBatches();
      if (viewingBatch?.BatchId === batch.BatchId) { setViewingBatch(null); }
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "批次刪除失敗。");
    }
  }

  return (
    <AdminLayout sectionLabel="網站設定" title="優惠碼設定" user={currentUser} onLogout={handleLogout}>
      <p className="mb-4 text-xs text-[#7b877f]">
        客人需在結帳頁主動輸入代碼才會生效，一組代碼可選擇「免運」「固定金額折抵商品小計」或「依比例折扣商品小計」其中一種效果。
        可以手動建立單一組給大家共用的代碼（設定總使用次數上限），也可以用「系統自動配置」一次產生一整批不重複的代碼，
        適合用於個別發放給客人的活動（例如每人限用一組的簡訊/Email 優惠碼）。
      </p>

      <form className="mb-4 flex flex-wrap items-center gap-2" onSubmit={handleSearch}>
        <select className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746]" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">全部狀態</option>
          <option value="Active">啟用</option>
          <option value="Inactive">停用</option>
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa6a0]" size={18} />
          <input className="h-11 w-full rounded-md border border-[#cfd8d2] pl-10 pr-3 font-normal outline-none focus:border-[#315746]" placeholder="搜尋代碼、說明或批次名稱" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="h-11 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" type="submit">搜尋</button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => { load(); loadBatches(); }} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="ml-auto inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={openCreateForm} type="button">
          <Plus size={20} />
          新增優惠碼
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      {/* 批次總覽：只列出曾用「系統自動配置」產生過的批次，手動建立的單一代碼不會出現在這裡。 */}
      {(batchesLoading || batches.length > 0) && (
        <div className="mb-4 rounded-md border border-[#dfe6e1] bg-white">
          <div className="flex items-center gap-2 border-b border-[#edf0ee] px-4 py-3">
            <Layers className="text-[#315746]" size={18} />
            <h2 className="text-sm font-bold text-[#315746]">批次總覽</h2>
          </div>
          <div className="overflow-auto">
            <table className="w-full min-w-[720px] table-fixed border-collapse text-left">
              <thead className="bg-[#f6f7f5] text-xs text-[#59665f]">
                <tr>
                  <th className="w-[220px] px-4 py-2.5">批次名稱</th>
                  <th className="w-[100px] px-4 py-2.5">總數</th>
                  <th className="w-[100px] px-4 py-2.5">已使用</th>
                  <th className="w-[100px] px-4 py-2.5">啟用中</th>
                  <th className="w-[160px] px-4 py-2.5">建立時間</th>
                  <th className="w-[140px] px-4 py-2.5">操作</th>
                </tr>
              </thead>
              <tbody>
                {batchesLoading ? (
                  <tr><td className="px-4 py-6 text-center text-sm text-[#59665f]" colSpan={6}>載入中...</td></tr>
                ) : batches.map((batch) => (
                  <tr className="border-t border-[#edf0ee] text-sm" key={batch.BatchId}>
                    <td className="truncate px-4 py-3 font-bold text-[#203d32]">{batch.BatchLabel || "（未命名批次）"}</td>
                    <td className="px-4 py-3">{batch.TotalCodes.toLocaleString()}</td>
                    <td className="px-4 py-3">{batch.UsedCodes.toLocaleString()}</td>
                    <td className="px-4 py-3">{batch.ActiveCodes.toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs text-[#7b877f]">{formatDateTime(batch.CreatedAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button className="inline-flex h-8 items-center justify-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-2.5 text-xs font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => handleViewBatch(batch)} type="button">
                          <Eye size={14} />
                          檢視
                        </button>
                        <button className="inline-flex h-8 items-center justify-center gap-1 rounded-md border border-[#f2b8b5] bg-white px-2.5 text-xs font-bold text-[#b3261e] transition hover:bg-[#fdebea]" onClick={() => handleDeleteBatch(batch)} type="button">
                          <Trash2 size={14} />
                          刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="overflow-auto">
          <table className="w-full min-w-[1080px] table-fixed border-collapse text-left">
            <thead className="bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[180px] px-4 py-3">優惠碼</th>
                <th className="w-[160px] px-4 py-3">折扣</th>
                <th className="w-[110px] px-4 py-3">最低金額</th>
                <th className="w-[110px] px-4 py-3">使用次數</th>
                <th className="w-[220px] px-4 py-3">生效區間</th>
                <th className="w-[80px] px-4 py-3">狀態</th>
                <th className="w-[160px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>載入中...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>尚未設定任何優惠碼。</td></tr>
              ) : items.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.ShippingCouponId}>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#eaf1fb] text-[#2b5a99]"><Ticket size={16} /></span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="truncate font-bold text-[#203d32]">{item.Code}</p>
                          {item.BatchId && <span className="shrink-0 rounded-full bg-[#f0ebfa] px-2 py-0.5 text-[10px] font-bold text-[#5b3f96]">批次</span>}
                        </div>
                        {item.Description && <p className="truncate text-xs text-[#7b877f]">{item.Description}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${item.DiscountType === "FreeShipping" ? "bg-[#eaf1fb] text-[#2b5a99]" : "bg-[#fdf3e7] text-[#9a6a1f]"}`}>
                      {describeDiscount(item)}
                    </span>
                  </td>
                  <td className="px-4 py-4">{item.MinOrderAmount !== null ? `滿 NT$ ${item.MinOrderAmount.toLocaleString()}` : "不限"}</td>
                  <td className="px-4 py-4">{item.RedemptionCount.toLocaleString()}{item.MaxRedemptions !== null ? ` / ${item.MaxRedemptions.toLocaleString()}` : ""}</td>
                  <td className="px-4 py-4 text-sm">
                    {item.StartAt || item.EndAt ? (
                      <>
                        {formatDateTime(item.StartAt)}
                        <br />~ {formatDateTime(item.EndAt)}
                      </>
                    ) : (
                      "不限期間"
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${item.IsActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>{item.IsActive ? "啟用" : "停用"}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="grid gap-2">
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => openEditForm(item)} type="button">
                        <Edit size={16} />
                        編輯
                      </button>
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#b3261e] transition hover:bg-[#fdebea]" onClick={() => handleDelete(item)} type="button">
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-[#edf0ee] px-4 py-3">
          <Pagination pagination={pagination} onChange={handlePageChange} />
        </div>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <button aria-label="關閉視窗" className="absolute inset-0 cursor-default" onClick={closeForm} type="button" />
          <div className="relative flex max-h-[90vh] w-full max-w-[760px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.2)] sm:rounded-md">
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-[#203d32]">
                  {editingItem ? "編輯優惠碼" : batchResult ? "批次產生完成" : "新增優惠碼"}
                </h2>
                <p className="mt-1 text-sm text-[#7b877f]">
                  {batchResult ? "請立即複製或匯出代碼清單，關閉視窗後仍可在下方「批次總覽」隨時查看。" : "客人於結帳頁輸入此代碼並驗證通過後，依所選折扣類型套用優惠。"}
                </p>
              </div>
              <button className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                <X size={18} />
              </button>
            </div>

            {batchResult ? (
              <div className="grid flex-1 gap-4 overflow-y-auto px-5 py-5">
                <div className="rounded-md border border-[#cfe3d6] bg-[#f1f8f3] px-4 py-3 text-sm text-[#245438]">
                  已成功產生 <strong>{batchResult.Items.length}</strong> 組代碼，批次名稱：<strong>{batchResult.BatchLabel}</strong>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => handleCopyCodes(batchResult.Items.map((item) => item.Code))} type="button">
                    <Copy size={16} />
                    複製全部代碼
                  </button>
                  <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => downloadCouponsCsv(`${batchResult.BatchLabel || "coupons"}.csv`, batchResult.Items)} type="button">
                    <Download size={16} />
                    下載 CSV
                  </button>
                  <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-60" disabled={exportingBatchId === batchResult.BatchId} onClick={() => handleExportBatchExcel(batchResult.BatchId)} type="button">
                    <FileSpreadsheet size={16} />
                    {exportingBatchId === batchResult.BatchId ? "匯出中..." : "下載 Excel"}
                  </button>
                  {copyFeedback && <span className="text-sm font-medium text-[#245438]">{copyFeedback}</span>}
                </div>
                <div className="max-h-[320px] overflow-auto rounded-md border border-[#edf0ee]">
                  <table className="w-full border-collapse text-left text-sm">
                    <thead className="sticky top-0 bg-[#f6f7f5] text-xs text-[#59665f]">
                      <tr>
                        <th className="px-3 py-2">代碼</th>
                        <th className="px-3 py-2">折扣</th>
                        <th className="px-3 py-2">可用次數上限</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batchResult.Items.map((item) => (
                        <tr className="border-t border-[#edf0ee]" key={item.ShippingCouponId}>
                          <td className="px-3 py-2 font-mono">{item.Code}</td>
                          <td className="px-3 py-2">{describeDiscount(item)}</td>
                          <td className="px-3 py-2">{item.MaxRedemptions !== null ? item.MaxRedemptions : "不限"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <form className="flex flex-1 flex-col overflow-hidden" onSubmit={createMode === "manual" ? handleSubmit : handleGenerateBatch}>
                <div className="grid flex-1 gap-4 overflow-y-auto px-5 py-5">
                  {formMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{formMessage}</div>}

                  {!editingItem && (
                    <AdminRadioCardGroup<CreateMode>
                      name="CreateMode"
                      value={createMode}
                      onChange={setCreateMode}
                      options={[
                        { value: "manual", label: "手動輸入單一代碼", description: "自訂一組代碼給大家共用，可設定總使用次數上限。" },
                        { value: "batch", label: "系統自動配置（批次）", description: "指定數量，系統自動產生多組不重複、各自獨立的代碼。" },
                      ]}
                    />
                  )}

                  {createMode === "manual" || editingItem ? (
                    <>
                      <label className="grid gap-2 text-sm font-bold text-[#315746]">
                        <span>優惠碼代碼 <span className="text-[#c3423f]">*</span></span>
                        <input className={inputClass} placeholder="例如 FREESHIP100" value={form.Code} onChange={(event) => setForm((current) => ({ ...current, Code: event.target.value.toUpperCase() }))} />
                      </label>
                      <label className="grid gap-2 text-sm font-bold text-[#315746]">
                        <span>說明（選填）</span>
                        <input className={inputClass} placeholder="給客服／內部辨識用的備註" value={form.Description} onChange={(event) => setForm((current) => ({ ...current, Description: event.target.value }))} />
                      </label>

                      <div className="grid gap-2">
                        <span className="text-sm font-bold text-[#315746]">折扣類型</span>
                        <AdminRadioCardGroup<CouponDiscountType>
                          name="DiscountType"
                          value={form.DiscountType}
                          onChange={(DiscountType) => setForm((current) => ({ ...current, DiscountType }))}
                          options={COUPON_DISCOUNT_TYPES.map((type) => ({
                            value: type,
                            label: discountTypeLabels[type],
                            icon: type === "FreeShipping" ? <Truck size={16} /> : type === "Percentage" ? <Percent size={16} /> : undefined,
                          }))}
                          columnsClassName="sm:grid-cols-3"
                        />
                      </div>
                      {form.DiscountType !== "FreeShipping" && (
                        <div className="grid gap-4 rounded-md border border-[#e3ded0] bg-[#fbfaf7] p-4 sm:grid-cols-2">
                          <label className="grid gap-2 text-sm font-bold text-[#315746]">
                            <span>{form.DiscountType === "Percentage" ? "折扣比例（%）" : "折抵金額（元）"} <span className="text-[#c3423f]">*</span></span>
                            <input className={inputClass} min={0} step={form.DiscountType === "Percentage" ? "0.1" : "1"} type="number" value={form.DiscountAmount} onChange={(event) => setForm((current) => ({ ...current, DiscountAmount: event.target.value }))} />
                          </label>
                          {form.DiscountType === "Percentage" && (
                            <label className="grid gap-2 text-sm font-bold text-[#315746]">
                              <span>折扣金額上限（選填）</span>
                              <input className={inputClass} min={0} placeholder="留空表示不限上限" type="number" value={form.MaxDiscountAmount} onChange={(event) => setForm((current) => ({ ...current, MaxDiscountAmount: event.target.value }))} />
                            </label>
                          )}
                          <p className="text-xs font-normal text-[#7b877f] sm:col-span-2">折扣依「商品小計」計算，不影響運費；折抵後的金額不會超過商品小計本身。</p>
                        </div>
                      )}

                      <div className="grid gap-4 sm:grid-cols-2">
                        <label className="grid gap-2 text-sm font-bold text-[#315746]">
                          <span>最低訂單金額（選填）</span>
                          <input className={inputClass} min={0} placeholder="留空表示不限金額" type="number" value={form.MinOrderAmount} onChange={(event) => setForm((current) => ({ ...current, MinOrderAmount: event.target.value }))} />
                        </label>
                        <label className="grid gap-2 text-sm font-bold text-[#315746]">
                          <span>總使用次數上限（選填）</span>
                          <input className={inputClass} min={0} placeholder="留空表示不限次數" type="number" value={form.MaxRedemptions} onChange={(event) => setForm((current) => ({ ...current, MaxRedemptions: event.target.value }))} />
                        </label>
                      </div>

                      <div className="grid gap-4">
                        <label className="grid gap-2 text-sm font-bold text-[#315746]">
                          <span>開始時間（選填）</span>
                          <input className={inputClass} type="datetime-local" value={form.StartAt} onChange={(event) => setForm((current) => ({ ...current, StartAt: event.target.value }))} />
                        </label>
                        <label className="grid gap-2 text-sm font-bold text-[#315746]">
                          <span>結束時間（選填）</span>
                          <input className={inputClass} type="datetime-local" value={form.EndAt} onChange={(event) => setForm((current) => ({ ...current, EndAt: event.target.value }))} />
                        </label>
                      </div>
                      <p className="-mt-2 text-xs text-[#9a8a7a]">開始／結束時間皆留空表示不限期間、立即生效直到手動停用或用完次數上限。</p>

                      <AdminSwitchCard checked={form.IsActive} description="停用後客人輸入此代碼會顯示無效。" label="啟用此優惠碼" onChange={(IsActive) => setForm((current) => ({ ...current, IsActive }))} />
                    </>
                  ) : (
                    <>
                      <label className="grid gap-2 text-sm font-bold text-[#315746]">
                        <span>批次（活動）名稱 <span className="text-[#c3423f]">*</span></span>
                        <input className={inputClass} placeholder="例如 母親節簡訊活動" value={batchForm.BatchLabel} onChange={(event) => setBatchForm((current) => ({ ...current, BatchLabel: event.target.value }))} />
                      </label>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <label className="grid gap-2 text-sm font-bold text-[#315746]">
                          <span>產生數量（1～500）<span className="text-[#c3423f]">*</span></span>
                          <input className={inputClass} max={500} min={1} type="number" value={batchForm.Quantity} onChange={(event) => setBatchForm((current) => ({ ...current, Quantity: event.target.value }))} />
                        </label>
                        <label className="grid gap-2 text-sm font-bold text-[#315746]">
                          <span>每組代碼可使用次數</span>
                          <input className={inputClass} min={1} placeholder="留空表示不限次數" type="number" value={batchForm.MaxRedemptionsPerCode} onChange={(event) => setBatchForm((current) => ({ ...current, MaxRedemptionsPerCode: event.target.value }))} />
                        </label>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <label className="grid gap-2 text-sm font-bold text-[#315746]">
                          <span>代碼前綴（選填）</span>
                          <input className={inputClass} placeholder="例如 VIP-（會加在每組代碼前面）" value={batchForm.CodePrefix} onChange={(event) => setBatchForm((current) => ({ ...current, CodePrefix: event.target.value.toUpperCase() }))} />
                        </label>
                        <label className="grid gap-2 text-sm font-bold text-[#315746]">
                          <span>代碼長度（4～20，不含前綴）</span>
                          <input className={inputClass} max={20} min={4} type="number" value={batchForm.CodeLength} onChange={(event) => setBatchForm((current) => ({ ...current, CodeLength: event.target.value }))} />
                        </label>
                      </div>
                      <p className="-mt-2 text-xs text-[#9a8a7a]">代碼只會使用不易混淆的英數字（排除 0/O、1/I/L），方便客人辨識與輸入。</p>

                      <div className="grid gap-2">
                        <span className="text-sm font-bold text-[#315746]">折扣類型</span>
                        <AdminRadioCardGroup<CouponDiscountType>
                          name="BatchDiscountType"
                          value={batchForm.DiscountType}
                          onChange={(DiscountType) => setBatchForm((current) => ({ ...current, DiscountType }))}
                          options={COUPON_DISCOUNT_TYPES.map((type) => ({
                            value: type,
                            label: discountTypeLabels[type],
                            icon: type === "FreeShipping" ? <Truck size={16} /> : type === "Percentage" ? <Percent size={16} /> : undefined,
                          }))}
                          columnsClassName="sm:grid-cols-3"
                        />
                      </div>
                      {batchForm.DiscountType !== "FreeShipping" && (
                        <div className="grid gap-4 rounded-md border border-[#e3ded0] bg-[#fbfaf7] p-4 sm:grid-cols-2">
                          <label className="grid gap-2 text-sm font-bold text-[#315746]">
                            <span>{batchForm.DiscountType === "Percentage" ? "折扣比例（%）" : "折抵金額（元）"} <span className="text-[#c3423f]">*</span></span>
                            <input className={inputClass} min={0} step={batchForm.DiscountType === "Percentage" ? "0.1" : "1"} type="number" value={batchForm.DiscountAmount} onChange={(event) => setBatchForm((current) => ({ ...current, DiscountAmount: event.target.value }))} />
                          </label>
                          {batchForm.DiscountType === "Percentage" && (
                            <label className="grid gap-2 text-sm font-bold text-[#315746]">
                              <span>折扣金額上限（選填）</span>
                              <input className={inputClass} min={0} placeholder="留空表示不限上限" type="number" value={batchForm.MaxDiscountAmount} onChange={(event) => setBatchForm((current) => ({ ...current, MaxDiscountAmount: event.target.value }))} />
                            </label>
                          )}
                          <p className="text-xs font-normal text-[#7b877f] sm:col-span-2">此折扣套用到整批所有代碼，依「商品小計」計算，不影響運費。</p>
                        </div>
                      )}

                      <label className="grid gap-2 text-sm font-bold text-[#315746]">
                        <span>最低訂單金額（選填）</span>
                        <input className={inputClass} min={0} placeholder="留空表示不限金額" type="number" value={batchForm.MinOrderAmount} onChange={(event) => setBatchForm((current) => ({ ...current, MinOrderAmount: event.target.value }))} />
                      </label>

                      <div className="grid gap-4">
                        <label className="grid gap-2 text-sm font-bold text-[#315746]">
                          <span>開始時間（選填）</span>
                          <input className={inputClass} type="datetime-local" value={batchForm.StartAt} onChange={(event) => setBatchForm((current) => ({ ...current, StartAt: event.target.value }))} />
                        </label>
                        <label className="grid gap-2 text-sm font-bold text-[#315746]">
                          <span>結束時間（選填）</span>
                          <input className={inputClass} type="datetime-local" value={batchForm.EndAt} onChange={(event) => setBatchForm((current) => ({ ...current, EndAt: event.target.value }))} />
                        </label>
                      </div>
                      <p className="-mt-2 text-xs text-[#9a8a7a]">此區間套用到整批所有代碼；開始／結束時間皆留空表示不限期間、立即生效。</p>

                      <AdminSwitchCard checked={batchForm.IsActive} description="停用後整批代碼都無法使用，可事後個別重新啟用。" label="產生後立即啟用" onChange={(IsActive) => setBatchForm((current) => ({ ...current, IsActive }))} />
                    </>
                  )}
                </div>

                <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
                  <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                    取消
                  </button>
                  {createMode === "manual" || editingItem ? (
                    <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">
                      <Save size={18} />
                      {saving ? "儲存中" : "儲存優惠碼"}
                    </button>
                  ) : (
                    <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={generatingBatch} type="submit">
                      <Layers size={18} />
                      {generatingBatch ? "產生中，請稍候..." : "產生批次代碼"}
                    </button>
                  )}
                </div>
              </form>
            )}

            {batchResult && (
              <div className="flex shrink-0 justify-end border-t border-[#edf0ee] px-5 py-4">
                <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={closeForm} type="button">
                  完成
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {viewingBatch && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <button aria-label="關閉視窗" className="absolute inset-0 cursor-default" onClick={() => setViewingBatch(null)} type="button" />
          <div className="relative flex max-h-[90vh] w-full max-w-[700px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.2)] sm:rounded-md">
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-[#203d32]">批次代碼：{viewingBatch.BatchLabel || "（未命名批次）"}</h2>
                <p className="mt-1 text-sm text-[#7b877f]">共 {viewingBatch.Items.length} 組代碼。</p>
              </div>
              <button className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => setViewingBatch(null)} type="button">
                <X size={18} />
              </button>
            </div>
            <div className="grid gap-4 overflow-y-auto px-5 py-5">
              {viewingBatchLoading ? (
                <p className="text-center text-sm text-[#59665f]">載入中...</p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => handleCopyCodes(viewingBatch.Items.map((item) => item.Code))} type="button">
                      <Copy size={16} />
                      複製全部代碼
                    </button>
                    <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => downloadCouponsCsv(`${viewingBatch.BatchLabel || "coupons"}.csv`, viewingBatch.Items)} type="button">
                      <Download size={16} />
                      下載 CSV
                    </button>
                    <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-60" disabled={exportingBatchId === viewingBatch.BatchId} onClick={() => handleExportBatchExcel(viewingBatch.BatchId)} type="button">
                      <FileSpreadsheet size={16} />
                      {exportingBatchId === viewingBatch.BatchId ? "匯出中..." : "下載 Excel"}
                    </button>
                    {copyFeedback && <span className="text-sm font-medium text-[#245438]">{copyFeedback}</span>}
                  </div>
                  <div className="max-h-[360px] overflow-auto rounded-md border border-[#edf0ee]">
                    <table className="w-full border-collapse text-left text-sm">
                      <thead className="sticky top-0 bg-[#f6f7f5] text-xs text-[#59665f]">
                        <tr>
                          <th className="px-3 py-2">代碼</th>
                          <th className="px-3 py-2">折扣</th>
                          <th className="px-3 py-2">已使用</th>
                          <th className="px-3 py-2">狀態</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewingBatch.Items.map((item) => (
                          <tr className="border-t border-[#edf0ee]" key={item.ShippingCouponId}>
                            <td className="px-3 py-2 font-mono">{item.Code}</td>
                            <td className="px-3 py-2">{describeDiscount(item)}</td>
                            <td className="px-3 py-2">{item.RedemptionCount}{item.MaxRedemptions !== null ? ` / ${item.MaxRedemptions}` : ""}</td>
                            <td className="px-3 py-2">{item.IsActive ? "啟用" : "停用"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog />
    </AdminLayout>
  );
}
